import { useRef } from 'react';
import type { ChangeEvent, KeyboardEvent } from 'react';
import { Box, Button, CircularProgress, TextField, Typography } from '@mui/material';
import { osirisTokens } from '@/theme/tokens';

export const PURPLE_FILL = '#6e44f5';
export const PURPLE_HOVER = '#826cfc';

interface AuthHeaderProps {
  title: string;
  subtitle?: React.ReactNode;
}

export function AuthHeader({ title, subtitle }: AuthHeaderProps) {
  return (
    <Box className="auth-header" sx={{ mb: 6 }}>
      <Typography
        className="auth-header-title"
        sx={{
          fontFamily: osirisTokens.fonts.display,
          fontSize: 24,
          fontWeight: 500,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: osirisTokens.text.primary,
          mb: 1,
        }}
      >
        {title}
      </Typography>
      {subtitle && (
        <Typography
          className="auth-header-subtitle"
          sx={{
            fontFamily: osirisTokens.fonts.body,
            fontSize: 14,
            color: osirisTokens.text.secondary,
            lineHeight: 1.5,
          }}
        >
          {subtitle}
        </Typography>
      )}
    </Box>
  );
}

interface AuthFieldLabelProps {
  children: React.ReactNode;
}

export function AuthFieldLabel({ children }: AuthFieldLabelProps) {
  return (
    <Typography
      className="auth-field-label"
      sx={{
        fontFamily: osirisTokens.fonts.display,
        fontSize: 14,
        letterSpacing: '0.04em',
        color: osirisTokens.text.muted,
        mb: 0.5,
      }}
    >
      {children}
    </Typography>
  );
}

interface AuthPrimaryButtonProps {
  type?: 'button' | 'submit';
  onClick?: () => void;
  isLoading?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
  className?: string;
}

export function AuthPrimaryButton({
  type = 'submit',
  onClick,
  isLoading = false,
  disabled = false,
  children,
  className,
}: AuthPrimaryButtonProps) {
  return (
    <Button
      type={type}
      onClick={onClick}
      disabled={disabled || isLoading}
      variant="contained"
      fullWidth
      className={className}
      startIcon={isLoading ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : undefined}
      sx={{
        height: 40,
        borderRadius: '4px',
        bgcolor: PURPLE_FILL,
        color: osirisTokens.text.primary,
        textTransform: 'uppercase',
        fontFamily: osirisTokens.fonts.display,
        fontSize: 13,
        fontWeight: 500,
        letterSpacing: '0.08em',
        boxShadow: 'none',
        '&:hover': { bgcolor: PURPLE_HOVER, boxShadow: 'none' },
        '&.Mui-disabled': {
          bgcolor: 'rgba(110, 68, 245, 0.4)',
          color: 'rgba(255, 255, 255, 0.6)',
        },
      }}
    >
      {children}
    </Button>
  );
}

interface AuthBottomNoteProps {
  children: React.ReactNode;
  className?: string;
}

export function AuthBottomNote({ children, className }: AuthBottomNoteProps) {
  return (
    <Typography
      className={`auth-bottom-note${className ? ' ' + className : ''}`}
      sx={{
        mt: 2,
        textAlign: 'center',
        fontFamily: osirisTokens.fonts.body,
        fontSize: 14,
        letterSpacing: '0.03em',
        color: osirisTokens.text.secondary,
        '& a, & button': {
          color: osirisTokens.text.active,
          textDecoration: 'none',
          cursor: 'pointer',
          background: 'none',
          border: 'none',
          padding: 0,
          font: 'inherit',
          '&:hover': { textDecoration: 'underline' },
        },
      }}
    >
      {children}
    </Typography>
  );
}

interface AuthCodeInputProps {
  value: string[];
  onChange: (next: string[]) => void;
  onComplete?: (code: string) => void;
  error?: boolean;
  autoFocus?: boolean;
}

export function AuthCodeInput({
  value,
  onChange,
  onComplete,
  error = false,
  autoFocus = true,
}: AuthCodeInputProps) {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const handleDigitChange = (index: number, raw: string) => {
    if (raw.length > 1) return;
    if (raw && !/^\d$/.test(raw)) return;
    const next = [...value];
    next[index] = raw;
    onChange(next);
    if (raw && index < 5) inputRefs.current[index + 1]?.focus();
    else if (raw && index === 5 && next.every((d) => d !== '')) onComplete?.(next.join(''));
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      if (!value[index] && index > 0) {
        const next = [...value];
        next[index - 1] = '';
        onChange(next);
        inputRefs.current[index - 1]?.focus();
      }
    } else if (e.key === 'v' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      navigator.clipboard.readText().then((text) => {
        const clean = text.replace(/\D/g, '').slice(0, 6);
        if (clean.length === 6) {
          onChange(clean.split(''));
          inputRefs.current[5]?.focus();
          onComplete?.(clean);
        }
      });
    }
  };

  return (
    <Box
      className="auth-code-input"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
      }}
    >
      {value.map((digit, index) => (
        <TextField
          key={index}
          className={`auth-code-digit-${index}`}
          value={digit}
          autoFocus={autoFocus && index === 0}
          onChange={(e: ChangeEvent<HTMLInputElement>) => handleDigitChange(index, e.target.value)}
          onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => handleKeyDown(index, e)}
          inputRef={(el: HTMLInputElement) => {
            inputRefs.current[index] = el;
          }}
          variant="outlined"
          slotProps={{
            htmlInput: {
              maxLength: 1,
              inputMode: 'numeric',
              pattern: '[0-9]*',
              autoComplete: 'off',
              'aria-label': `Digit ${index + 1}`,
              // keep password managers / autofill popups away from code boxes
              'data-1p-ignore': 'true',
              'data-lpignore': 'true',
              'data-bwignore': 'true',
              'data-form-type': 'other',
              style: {
                textAlign: 'center',
                fontFamily: osirisTokens.fonts.mono,
                fontSize: 24,
                letterSpacing: '0.06em',
                padding: 0,
              },
            },
          }}
          sx={{
            minHeight: 'unset',
            width: 40,
            '& .MuiOutlinedInput-root': {
              height: 48,
              borderRadius: '4px',
              bgcolor: 'transparent',
              '& fieldset': {
                borderColor: error ? osirisTokens.text.error : osirisTokens.border.lighter,
              },
              '&:hover fieldset': {
                borderColor: error ? osirisTokens.text.error : osirisTokens.border.active,
              },
              '&.Mui-focused fieldset': {
                borderColor: error ? osirisTokens.text.error : osirisTokens.border.active,
                borderWidth: 1,
              },
              '&.Mui-focused input': {
                color: osirisTokens.text.active,
                caretColor: osirisTokens.text.active,
              },
              '& input': { color: osirisTokens.text.primary, caretColor: osirisTokens.text.active },
            },
          }}
        />
      ))}
    </Box>
  );
}
